import React, {useEffect, useState} from "react"
import {LineItem, PurcsData} from "../../../dtos/orderResponse"
import Price from "../../formatting/Price"
import Tax from "../../formatting/Tax"
import Document from "../Document"
import {LineItemAction} from "./lineItemActions/LineItemActions"
import {RefundInfo} from "../../../dtos/refundInfo"
import LineItemState from "./LineItemState"
import ReplacementHistory from "./ReplacementHistory"
import {Event} from "../../../dtos/eventResponse"
import {getCaseId, getQueryParameters, isValidUrl} from "../../../utils/UrlParser"
import AdditionalFees from "./AdditionalFees"
import * as Order from "../../../dtos/orderResponse"
import {
	getShippingEventsPerItem,
	getDeliveryEventsPerItem,
	getRefundEventsPerItem,
	getRefundRequestEventsPerItem,
	getFulfillmentAcceptedEventsPerItem,
	getMcpItemId,
	getStateChangedEventsPerItem,
	getInvalidReplacementEventsPerItem,
	getDeliveryUpdatedEvents,
	getItemStatus,
} from "../../../services/order/eventHelper"
import {OrderCredits} from "../../../services/order/CreditDetails"
import OrderService from "../../../services/order/OrderService"
import {CommerceItem} from "../../../services/order/Order"
import DisplayProductAttributes from "./DisplayProductAttributes"
import {postActionLog, ButtonActionTypes} from "../../../services/order/TrackOrderActions"
import {getDeliveryURLOfItemID} from "../../../services/order/LineItemTrackingHelper"
import {makeStyles} from "tss-react/mui"
import {
	Card,
	CardContent,
	CardHeader,
	Checkbox,
	FormControlLabel,
	Grid,
	Typography,
} from "@mui/material"
import SwanConfiguration from "../design/SwanConfiguration"
import {FulfillmentStatusBanner, ApplicationName, TenantEnvironment} from "@vp/fulfillment-history"
import {useQuery} from "@tanstack/react-query"
import DocumentEditHistory from "./DocumentEditHistory/DocumentEditHistory"

const useStyles = makeStyles()({
	cardTitle: {
		overflowWrap: "break-word",
		width: "230px",
		display: "flex",
		alignItems: "center",
	},
})

const orderService = new OrderService()
interface LineItemProps {
	customerId: string
	orderId: string
	order: Order.Order
	lineItem: LineItem
	orderNumber: string
	orderIsLegacy: boolean
	updateParent: () => {}
	orderRefInfo: RefundInfo
	culture: string
	itemsNumber: number
	tenantId: string
	isAnonymousCustomer: boolean
	events: Event[]
	cartId: string
	purcsData: PurcsData
	tenantForPurcs: string
	isFullyPaidWithMC: boolean
	paymentFullySuccessful: boolean
	orderCredits: OrderCredits | null
	orderCreationDate: Date
	orderIsReleasedToFulfillment: boolean
	isVCSAgent: boolean
	isVCSOrder: boolean
	isSelectedItem?: any
	onCheckboxChange?: (lineItem: LineItem, checked: any) => void
	isChecked?: boolean
	defaultCountryCode: string
	itemKeyEvents: Event[]
	isPayOnBillingAccountOrder: boolean
	isCompanyCardOrder: boolean
}

export default function LineItemDetails(props: LineItemProps) {
	const {classes} = useStyles()
	const multiSelectPermission = !props.isVCSAgent && props.isVCSOrder
	const {lineItem} = props
	const [shippingEventsPerItem, setShippingEventsPerItem] = useState(new Map<string, Event[]>([]))
	const [deliveryEventsPerItem, setDeliveryEventsPerItem] = useState(new Map<string, Event[]>([]))
	const [refundEventsPerItem, setRefundEventsPerItem] = useState(new Map<string, Event[]>([]))
	const [deliveryDetails, setDeliveryUpdate] = useState(new Map<string, Event[]>([]))
	const [invalidReplacementEventsPerItem, setInvalidReplacementEventsPerItem] = useState(
		new Map<string, Event[]>([])
	)
	const [refundRequestEventsPerItem, setRefundRequestEventsPerItem] = useState(
		new Map<string, Event[]>([])
	)
	const [stateChangedEventsPerItem, setStateChangedEventsPerItem] = useState(
		new Map<string, Event[]>([])
	)
	const [fulfillerName, setFulfillerName] = useState("")

	// Care Console fetches the BOP fulfillment history itself (using Cimpress auth)
	// and passes it to the banner as data, so the banner doesn't rely on @vp/auth.
	// The query is keyed by order so all line items in an order share one request.
	const {data: orderFulfillmentHistory} = useQuery({
		queryKey: ["bopFulfillmentHistory", props.orderNumber],
		queryFn: () => orderService.getOrderFulfillmentHistory(props.customerId, props.orderNumber),
		enabled: !!props.customerId && !!props.orderNumber,
		retry: false,
	})
	const lineItemFulfillmentHistory =
		orderFulfillmentHistory?.lineItems?.find((li) => li.lineItemId === lineItem.id) ?? null

	const handleChange = (event: any) => {
		if (props.onCheckboxChange) props.onCheckboxChange(props.lineItem, event.target.checked)
	}

	useEffect(() => {
		setShippingEventsPerItem(getShippingEventsPerItem(props.events))
		setDeliveryEventsPerItem(getDeliveryEventsPerItem(props.events))
		setRefundEventsPerItem(getRefundEventsPerItem(props.events))
		setRefundRequestEventsPerItem(getRefundRequestEventsPerItem(props.events))
		setStateChangedEventsPerItem(getStateChangedEventsPerItem(props.events))
		setInvalidReplacementEventsPerItem(getInvalidReplacementEventsPerItem(props.events))
		setDeliveryUpdate(getDeliveryUpdatedEvents(props.events))

		const fulfillmentAcceptedEventsPerItem = getFulfillmentAcceptedEventsPerItem(props.events)

		if (props.events && props.events.length > 0 && (!fulfillerName || fulfillerName === "")) {
			const getFulfillerName = async () => {
				const events = fulfillmentAcceptedEventsPerItem.get(lineItem.id)
				let name = "Unknown"
				if (events && events.length > 0) {
					name =
						events[0].eventProperties &&
						events[0].eventProperties.details &&
						events[0].eventProperties.details.length > 0 &&
						events[0].eventProperties.details[0].additionalData &&
						events[0].eventProperties.details[0].additionalData.fulfillerName
							? events[0].eventProperties.details[0].additionalData.fulfillerName
							: "Unknown"
				} else {
					const mcpItemId = getMcpItemId(props.events, props.lineItem.id)
					const commerceOrder = await orderService.getCommerceOrder(
						mcpItemId,
						props.orderCreationDate
					)
					if (commerceOrder && commerceOrder.items) {
						const targetItem = commerceOrder.items.find(
							(item: CommerceItem) =>
								item.itemId === mcpItemId || item.merchantItemId === lineItem.id
						)
						if (targetItem && targetItem.fulfillerId) {
							name = targetItem.fulfillerId === "1" ? "Vistaprint" : "3PF"
						} else {
							name = "Unknown"
						}
					} else {
						name = "Unknown"
					}
				}
				setFulfillerName(name)
			}
			if (props.events && props.events.length > 0) getFulfillerName()
		}
		// eslint-disable-next-line
	}, [props.events])

	const getShippedEventByTrackingId = (trackingId: string): Event | undefined => {
		return shippingEventsPerItem.get(lineItem.id)
			? shippingEventsPerItem
					.get(lineItem.id)!
					.filter(
						(x: Event) =>
							x.itemInfo &&
							x.itemInfo.length > 0 &&
							x.itemInfo[0].itemProperties &&
							x.itemInfo[0].itemProperties.trackingId &&
							trackingId === x.itemInfo[0].itemProperties.trackingId
					)[0]
			: undefined
	}

	const getDeliveryEventByTrackingId = (trackingId: string): Event | undefined => {
		return deliveryEventsPerItem.get(lineItem.id)
			? deliveryEventsPerItem
					.get(lineItem.id)!
					.filter(
						(x: Event) =>
							x.eventProperties &&
							x.eventProperties.details &&
							x.eventProperties.details.length > 0 &&
							x.eventProperties.details[0] &&
							x.eventProperties.details[0].additionalData &&
							x.eventProperties.details[0].additionalData.trackingId &&
							trackingId === x.eventProperties.details[0].additionalData.trackingId
					)[0]
			: undefined
	}

	const getQuantityOfTrackingId = (trackingId: string): string | number => {
		const event = getShippedEventByTrackingId(trackingId)
		return event ? event.itemInfo[0].quantity : "No data"
	}

	const getShippingDateOfTrackingId = (trackingId: string): string => {
		const event = getShippedEventByTrackingId(trackingId)
		return event ? new Date(event.dateTime).toDateString() : "Not yet"
	}

	const getDeliveryDateOfTrackingId = (trackingId: string): string => {
		const event = getDeliveryEventByTrackingId(trackingId)
		return event ? new Date(event.dateTime).toDateString() : ""
	}
	const getOptionSelections = () => {
		if (props.lineItem.fulfillmentOptions) {
			return props.lineItem.fulfillmentOptions
		}
		if (props.purcsData && props.purcsData.optionSelections) return props.purcsData.optionSelections
		if (
			props.lineItem.customOrderData &&
			props.lineItem.customOrderData.modData &&
			props.lineItem.customOrderData.modData.merchandisingSelections
		)
			return props.lineItem.customOrderData.modData.merchandisingSelections
		return {}
	}

	const getPreviewInstructionsUri = () => {
		if (props.purcsData && props.purcsData.previewInstructionsUri)
			return props.purcsData.previewInstructionsUri
		if (props.lineItem.livePreviewUrl) {
			let previewInstructionsUri = props.lineItem.livePreviewUrl
			if (
				isValidUrl(props.lineItem.livePreviewUrl) &&
				props.lineItem.livePreviewUrl.includes("previewInstructionsUri")
			) {
				const urlParams = new URL(props.lineItem.livePreviewUrl).searchParams
				const previewInstructionsUriParam = urlParams.get("previewInstructionsUri") as string
				if (previewInstructionsUriParam) {
					previewInstructionsUri = previewInstructionsUriParam
				}
			}
			return previewInstructionsUri
		}
		return ""
	}
	function documentIdIsAvailable() {
		return (
			lineItem &&
			(lineItem as any).customOrderData &&
			(((lineItem as any).customOrderData.analytics &&
				(lineItem as any).customOrderData.analytics.workId) ||
				((lineItem as any).customOrderData.fulfillment &&
					(lineItem as any).customOrderData.fulfillment.documentId))
		)
	}

	function getWorkRevisionId() {
		if (
			props.lineItem.customOrderData &&
			props.lineItem.customOrderData.analytics &&
			props.lineItem.customOrderData.analytics.workRevisionId
		) {
			return props.lineItem.customOrderData.analytics.workRevisionId
		} else if (props.purcsData && props.purcsData.workRevisionId) {
			return props.purcsData.workRevisionId
		} else {
			return ""
		}
	}

	function getDocumentUrl() {
		return props.orderIsLegacy ? props.lineItem.livePreviewUrl : props.lineItem.documentUrl
	}

	function getWorkId() {
		const queryParams = getQueryParameters((lineItem as any).editDocumentUrl)
		if (queryParams["workId"]) {
			return queryParams["workId"]
		} else {
			return (lineItem as any).customOrderData?.fulfillment?.documentId
				? (lineItem as any).customOrderData?.fulfillment?.documentId
				: (lineItem as any).customOrderData?.analytics?.workId
		}
	}

	function getRequestId() {
		if (lineItem.customOrderData && (lineItem.customOrderData as any).fulfillment) {
			return (lineItem.customOrderData as any).fulfillment.RequestAltId
		} else {
			return null
		}
	}
	const logClickTrackingNumber = (trackingId: string) => {
		try {
			const caseId = getCaseId()
			postActionLog(
				{
					event: ButtonActionTypes.OpenTrackingUrl,
					actionMetaData: {
						lineItemId: props.lineItem.id,
						orderNumber: props.orderNumber,
						tenantId: props.tenantId,
						trackingId,
					},
				},
				caseId
			)
		} catch (err) {
			console.log("error when sending log data ")
		}
	}
	// ToDo: Use this for shipment info when available
	// function hasShipmentDetails(): boolean {
	//     return lineItem.shipmentDetail !== undefined
	// }

	return (
		<Card
			raised={true}
			style={{
				minWidth: "300px",
				maxWidth: "300px",
				minHeight: "100%",
				borderRadius: "8px",
			}}
		>
			<CardHeader
				titleTypographyProps={{variant: "h6", noWrap: false}}
				title={
					<div className={classes.cardTitle}>
						{!multiSelectPermission && (
							<FormControlLabel
								control={
									<Checkbox
										checked={props.isChecked}
										onChange={handleChange}
										value={"id"}
										color="primary"
									/>
								}
								label={""}
							/>
						)}
						{lineItem.productName ? lineItem.productName : "Unknown"}
					</div>
				}
				action={
					<LineItemAction
						customerId={props.customerId}
						orderId={props.orderId}
						order={props.order}
						lineItem={lineItem}
						updateLineItem={props.updateParent}
						orderNumber={props.orderNumber}
						orderRefInfo={props.orderRefInfo}
						orderIsLegacy={props.orderIsLegacy}
						culture={props.culture}
						isAnonymousCustomer={props.isAnonymousCustomer}
						tenantId={props.tenantId}
						cartId={props.cartId}
						isFullyPaidWithMC={props.isFullyPaidWithMC}
						paymentFullySuccessful={props.paymentFullySuccessful}
						isVCSAgent={props.isVCSAgent}
						isVCSOrder={props.isVCSOrder}
						historicalRefundData={{
							refundEvents: refundEventsPerItem.get(lineItem.id),
							refundRequestEvents: refundRequestEventsPerItem.get(lineItem.id),
						}}
						orderCredits={props.orderCredits}
						orderIsReleasedToFulfillment={props.orderIsReleasedToFulfillment}
						stateChangedEvents={stateChangedEventsPerItem.get(lineItem.id)}
						events={props.events}
						defaultCountryCode={props.defaultCountryCode}
						isPayOnBillingAccountOrder={props.isPayOnBillingAccountOrder}
						isCompanyCardOrder={props.isCompanyCardOrder}
					/>
				}
			/>
			<div>
				<Document
					orderIsLegacy={props.orderIsLegacy}
					documentReferenceUrl={getDocumentUrl()}
					tenantId={props.tenantId}
					workRevisionId={getWorkRevisionId()}
					livePreviewUrl={props.lineItem.livePreviewUrl || getDocumentUrl()}
					events={props.events}
					lineItemId={lineItem.id}
					locale={props.culture}
					productKey={props.lineItem.productKey}
					purcsData={props.purcsData}
					tenantForPurcs={props.tenantForPurcs}
					customOrderData={props.lineItem.customOrderData}
					previewInstructionsUri={getPreviewInstructionsUri()}
					optionSelection={getOptionSelections()}
				/>

				<CardContent>
					<LineItemState
						itemNumber={props.itemsNumber}
						orderNumber={props.orderNumber}
						lineItem={lineItem}
						trackingDetails={deliveryDetails}
						itemStatus={getItemStatus(props.itemKeyEvents)}
					/>
					<div
						style={{
							marginTop: "5px",
							wordWrap: "break-word",
							overflowWrap: "break-word",
							wordBreak: "break-word",
							whiteSpace: "normal",
							maxWidth: "100%",
						}}
					>
						{lineItemFulfillmentHistory && (
							<SwanConfiguration>
								<FulfillmentStatusBanner
									applicationName={ApplicationName.CARE_CONSOLE}
									debug={
										process.env.REACT_APP_DEV_ENVIRONMENT &&
										process.env.REACT_APP_DEV_ENVIRONMENT === "PRODUCTION"
											? false
											: true
									}
									locale={props.culture}
									orderNumber={props.orderNumber}
									shopperId={props.customerId}
									tenantEnvironment={
										process.env.REACT_APP_DEV_ENVIRONMENT &&
										process.env.REACT_APP_DEV_ENVIRONMENT === "PRODUCTION"
											? TenantEnvironment.Production
											: TenantEnvironment.Staging
									}
									lineItemFulfillmentHistory={lineItemFulfillmentHistory}
									lineItemDetails={{
										id: lineItem.id,
										design: {
											livePreviewUrl: lineItem.livePreviewUrl || getDocumentUrl(),
										},
										product: {
											merchantProductKey: lineItem.productKey,
											merchandisedProductName: lineItem.productName,
										},
									}}
								/>
							</SwanConfiguration>
						)}
					</div>

					<Typography>
						{!lineItem.trackingData || lineItem.trackingData.length === 0 ? (
							getDeliveryURLOfItemID(deliveryDetails, lineItem.id) ? (
								<>
									<b>Tracking Information: </b>
									<Typography>
										<a
											href={getDeliveryURLOfItemID(deliveryDetails, lineItem.id)}
											target="_blank"
											rel="noopener noreferrer"
											onClick={() => {
												getDeliveryURLOfItemID(deliveryDetails, lineItem.id)
											}}
										>
											{"Shipment # " + lineItem.quantity}
										</a>{" "}
									</Typography>
								</>
							) : (
								<b>No tracking URL is available</b>
							)
						) : (
							<div>
								<b>Tracking Information: </b>
								{lineItem.trackingData.map((elt) => (
									<Typography style={{paddingLeft: "5px", marginBottom: "5px"}} variant="body1">
										<a
											href={elt.trackingUrl}
											target="_blank"
											rel="noopener noreferrer"
											onClick={() => {
												logClickTrackingNumber(elt.trackingId)
											}}
										>
											{`Shipment #${lineItem.trackingData.indexOf(elt) + 1}  `}
											(QTY: {getQuantityOfTrackingId(elt.trackingId)})
										</a>{" "}
										<br />
										Shipped on <b>{getShippingDateOfTrackingId(elt.trackingId)}</b>
										<br />
										{getDeliveryDateOfTrackingId(elt.trackingId) !== "" && (
											<>
												Delivered on <b>{getDeliveryDateOfTrackingId(elt.trackingId)}</b>
											</>
										)}
									</Typography>
								))}
							</div>
						)}
					</Typography>
					<Typography>
						Expected by :{" "}
						<b>
							{(lineItem as any).promisedArrivalDate
								? (lineItem as any).promisedArrivalDate
								: "Not available"}
						</b>
					</Typography>
					<ReplacementHistory
						replacementHistory={lineItem.replacementHistory ? lineItem.replacementHistory : []}
						lastReplacementFailure={invalidReplacementEventsPerItem.get(lineItem.id)}
					/>
					<div>
						<DocumentEditHistory
							itemKeyEvents={props.itemKeyEvents}
							locale={props.culture}
							productKey={props.lineItem.productKey}
							previewInstructionsUri={getPreviewInstructionsUri()}
							optionSelection={getOptionSelections()}
							lineItem={props.lineItem}
							updateLineItem={props.updateParent}
							orderNumber={props.orderNumber}
							documentId={getWorkId()}
						/>
					</div>
					{lineItem.corePricing && lineItem.corePricing.additionalFees && (
						<AdditionalFees
							additionalFees={lineItem.corePricing.additionalFees}
							currency={lineItem.totalListPrice.currencyCode}
							fractionDigits={lineItem.totalListPrice.fractionDigits}
						/>
					)}
					<Typography style={{fontWeight: "bold", display: "inline-block"}}>
						Item Details
					</Typography>
					<Grid container justifyContent="space-between">
						<Grid item xs={6}>
							<Typography variant="subtitle1">Quantity</Typography>
						</Grid>
						<Grid item xs={6}>
							<Typography variant="subtitle1" style={{fontWeight: 600}}>
								{lineItem.quantity}
							</Typography>
						</Grid>
						<Grid item xs={6}>
							<Typography variant="subtitle1">Price</Typography>
						</Grid>
						<Grid item xs={6}>
							<Typography
								variant="caption"
								style={{fontWeight: 300, color: "gray", fontSize: "11px"}}
							>
								<Tax
									taxedValue={
										lineItem.pricingBreakdownV3.totalDiscountedPriceCents
											? lineItem.pricingBreakdownV3.totalDiscountedPriceCents.taxed
											: 0
									}
									untaxedValue={
										lineItem.pricingBreakdownV3.totalDiscountedPriceCents
											? lineItem.pricingBreakdownV3.totalDiscountedPriceCents.untaxed
											: 0
									}
									fractionDigits={lineItem.totalListPrice.fractionDigits}
									currency={lineItem.totalListPrice.currencyCode}
								/>
							</Typography>
						</Grid>
						<br />
						<Grid item xs={6}></Grid>
						<Grid container direction="row" item xs={6}>
							{lineItem.totalListPrice.centAmount === lineItem.totalDiscountedPrice.centAmount ? (
								<></>
							) : (
								<Grid item xs={6}>
									<Typography variant="subtitle1" display="block" style={{fontWeight: 600}}>
										<Price
											value={String(lineItem.totalListPrice.centAmount)}
											fractionDigits={lineItem.totalListPrice.fractionDigits}
											currency={lineItem.totalListPrice.currencyCode}
											discounted={true}
										/>
									</Typography>
								</Grid>
							)}
							<Grid item xs={6}>
								<Typography variant="subtitle1" style={{fontWeight: 600}}>
									<Price
										value={String(lineItem.totalDiscountedPrice.centAmount)}
										fractionDigits={lineItem.totalListPrice.fractionDigits}
										currency={lineItem.totalListPrice.currencyCode}
									/>
								</Typography>
							</Grid>
						</Grid>

						<Grid item xs={6}>
							<Typography variant="subtitle1">Shipping</Typography>
						</Grid>
						<Grid item xs={6}>
							<Typography
								variant="caption"
								style={{fontWeight: 300, color: "gray", fontSize: "11px"}}
							>
								<Tax
									taxedValue={lineItem?.shippingDiscountedTaxPrice?.centAmount ?? 0}
									untaxedValue={lineItem?.shippingDiscountedPrice?.centAmount ?? 0}
									fractionDigits={lineItem?.totalListPrice?.fractionDigits}
									currency={lineItem?.totalListPrice?.currencyCode}
								/>
							</Typography>
						</Grid>
						<br />
						<Grid item xs={6}></Grid>
						<Grid container direction="row" item xs={6}>
							{lineItem.totalListPrice.centAmount === lineItem.totalDiscountedPrice.centAmount ? (
								<></>
							) : (
								<Grid item xs={6}>
									<Typography variant="subtitle1" display="block" style={{fontWeight: 600}}>
										<Price
											value={String(lineItem?.shippingTaxPrice?.centAmount)}
											fractionDigits={lineItem?.totalListPrice?.fractionDigits}
											currency={lineItem?.totalListPrice?.currencyCode}
											discounted={true}
										/>
									</Typography>
								</Grid>
							)}
							<Grid item xs={6}>
								<Typography variant="subtitle1" style={{fontWeight: 600}}>
									<Price
										value={String(lineItem?.shippingDiscountedTaxPrice?.centAmount)}
										fractionDigits={lineItem?.totalListPrice?.fractionDigits}
										currency={lineItem?.totalListPrice?.currencyCode}
									/>
								</Typography>
							</Grid>
						</Grid>
					</Grid>
					{
						<DisplayProductAttributes
							displayProductAttribute={lineItem.displayProductAttributes}
							orderNumber={props.orderNumber}
							lineItemId={props.lineItem.id}
						></DisplayProductAttributes>
					}
					{documentIdIsAvailable() && (
						<Typography variant="subtitle1" key="workId">
							Doc ID: {getWorkId()}
						</Typography>
					)}
					{lineItem.altDocId && (
						<Typography variant="subtitle1" key="altDocId">
							Legacy Doc ID: {lineItem.altDocId}
						</Typography>
					)}
					{getRequestId() && (
						<Typography variant="subtitle1" key="requestId">
							Request ID: {getRequestId()}
						</Typography>
					)}
					{lineItem.id && (
						<Typography variant="subtitle1" key="altDocId">
							Item ID: {lineItem.id}
						</Typography>
					)}
					{fulfillerName && (
						<Typography
							style={{fontWeight: "bold", display: "inline-block"}}
							key="fulfillmentMerchantId"
						>
							Fulfiller :{" "}
							{fulfillerName.toLowerCase() !== "unknown"
								? fulfillerName.toLowerCase() === "vistaprint"
									? "In house"
									: "3rd Party Fullfiller"
								: "Fulfiller not yet assigned"}
						</Typography>
					)}
				</CardContent>
			</div>
		</Card>
	)
}
